import * as THREE from "three";
import { buildSolCoin } from "./coin";
import { buildEthCoin } from "./ethCoin";
import { HaloSprite } from "./fx";

/**
 * CoinField — every coin of one kind on the road in a handful of draw calls: one InstancedMesh per part of the coin
 * model (built from the same prototype as buildSolCoin / buildEthCoin, so the same geometry, materials and look) and
 * one instanced camera-facing quad for the glow sprites (the HaloSprite: pushed away from the camera behind its coin).
 *
 *   const sol = new CoinField("sol");  scene.add(sol.root);
 *   sol.add(entity.id, x, yBase + 0.75, z);      // add — or move, if the id is already there (call it every frame)
 *   sol.remove(entity.id);                       // collected / scrolled away
 *   sol.update(clock);                           // once per frame, after the adds: spin + bob, no allocation
 *
 * SOL: y = the coin's centre (the scene's yBase + 0.75); update adds the bob (sin(t·5 + phase) · 0.06) and spins it
 * (t · 5.5 + phase · 0.7). ETH: y = the road under it (yBase); update wobbles it (sin(t·2 + phase) · 0.35) and pulses
 * the glow (2.9 · (0.88 + 0.14 sin(t·6 + phase))). phase defaults to the id — the same motion animateProp gives.
 * Draw calls: SOL 2 (coin + glows); ETH 7 (stripes, base, base light, coin, coin light, glows, blob shadow).
 */
export type CoinKind = "sol" | "eth";

interface Part {
  mesh: THREE.InstancedMesh;
  /** the part's matrix inside the coin (root space, or spin space when `spins`) */
  local: THREE.Matrix4;
  identity: boolean;
  spins: boolean;
}

interface GlowSpec { material: THREE.SpriteMaterial; size: number; push: number; offset: THREE.Vector3 }

const _m = new THREE.Matrix4();
const _base = new THREE.Matrix4();
const _spin = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _axisY = new THREE.Vector3(0, 1, 0);
const _sphere = new THREE.Sphere();

/** The glow quads: a HaloSprite per instance (centre and size from the instance matrix), additive, not tone mapped. */
function glowMaterial(spec: GlowSpec): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog, { map: { value: null }, color: { value: new THREE.Color() }, opacity: { value: 1 }, push: { value: 0 } }]),
    vertexShader: /* glsl */`
      #include <common>
      #include <fog_pars_vertex>
      uniform float push;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        vec4 centre = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        float size = length((modelMatrix * instanceMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
        centre.xyz += normalize(centre.xyz - cameraPosition) * push;
        vec4 mvPosition = viewMatrix * centre;
        mvPosition.xy += position.xy * size;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */`
      #include <common>
      #include <fog_pars_fragment>
      uniform sampler2D map;
      uniform vec3 color;
      uniform float opacity;
      varying vec2 vUv;
      void main() {
        vec4 texel = texture2D(map, vUv);
        gl_FragColor = vec4(color * texel.rgb, opacity * texel.a);
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
    transparent: true,
    depthWrite: false,
    blending: spec.material.blending,
    fog: true
  });
}

export class CoinField {
  /** add this to the scene once (it stays at the origin: the instances carry world positions) */
  readonly root = new THREE.Group();
  private parts: Part[] = [];
  private glow?: { mesh: THREE.InstancedMesh; spec: GlowSpec };
  private capacity: number;
  private ids: number[] = [];
  private slots = new Map<number, number>();
  private pos: Float32Array;
  private phase: Float32Array;
  private scale: Float32Array;
  /** the glow's and parts' reach beyond an instance's anchor (for the bounding sphere) */
  private reach = 0;
  private readonly bounds = new THREE.Sphere(new THREE.Vector3(), 0);
  /** where the spinning part sits above the coin's anchor (ETH: the coin over its base) */
  private readonly spinOffset = new THREE.Vector3();
  /** night (night.ts): the glows grow and brighten; 1 by day */
  private glowBoost = 1;

  constructor(readonly kind: CoinKind, capacity = 64) {
    this.capacity = Math.max(1, capacity);
    this.pos = new Float32Array(this.capacity * 3);
    this.phase = new Float32Array(this.capacity);
    this.scale = new Float32Array(this.capacity);
    this.root.name = kind === "sol" ? "solCoins" : "ethCoins";
    const proto = kind === "sol" ? buildSolCoin() : buildEthCoin();
    proto.updateMatrixWorld(true);
    const rootInverse = new THREE.Matrix4().copy(proto.matrixWorld).invert();
    const spinGroup = proto.getObjectByName("spin");
    const spinInverse = spinGroup ? new THREE.Matrix4().copy(spinGroup.matrixWorld).invert() : undefined;
    proto.traverse((o) => {
      if (o instanceof HaloSprite || (o as THREE.Sprite).isSprite) {
        const sprite = o as HaloSprite;
        const spec: GlowSpec = { material: sprite.material as THREE.SpriteMaterial, size: sprite.scale.x, push: (sprite as HaloSprite).push ?? 0, offset: sprite.position.clone() };
        const material = glowMaterial(spec);
        material.uniforms.map.value = spec.material.map;
        (material.uniforms.color.value as THREE.Color).copy(spec.material.color);
        material.uniforms.opacity.value = spec.material.opacity;
        material.uniforms.push.value = spec.push;
        const mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), material, this.capacity);
        mesh.name = "glow";
        mesh.renderOrder = sprite.renderOrder;
        mesh.count = 0;
        this.glow = { mesh, spec };
        this.reach = Math.max(this.reach, spec.offset.length() + spec.size * 0.6 + spec.push);
        return;
      }
      const source = o as THREE.Mesh;
      if (!source.isMesh) return;
      let spins = false;
      for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p === spinGroup) spins = true;
      // a part under "spin" keeps its matrix inside the spin group (the group itself is rebuilt per instance)
      const local = new THREE.Matrix4().multiplyMatrices(spins && spinInverse ? spinInverse : rootInverse, source.matrixWorld);
      const mesh = new THREE.InstancedMesh(source.geometry, source.material, this.capacity);
      mesh.name = source.name || source.parent?.name || "part";
      mesh.renderOrder = source.renderOrder;
      mesh.count = 0;
      const material = Array.isArray(source.material) ? source.material[0] : source.material;
      const opaque = !material.transparent && material.blending === THREE.NormalBlending && !(material as THREE.MeshBasicMaterial).isMeshBasicMaterial;
      // the scene's rules (GameScene.prepareEntity): SOL casts no sun shadow; on ETH the coin itself (≥ 0.7 m) casts and every part takes it
      if (!source.geometry.boundingSphere) source.geometry.computeBoundingSphere();
      const radius = source.geometry.boundingSphere?.radius ?? 0;
      mesh.castShadow = kind === "eth" && opaque && radius >= 0.7;
      mesh.receiveShadow = kind === "eth" && mesh.name !== "blobShadow";
      this.parts.push({ mesh, local, identity: local.equals(new THREE.Matrix4()), spins });
      const spinOffset = spins && spinGroup ? spinGroup.position.length() : 0;
      this.reach = Math.max(this.reach, spinOffset + _p.setFromMatrixPosition(local).length() + radius);
    });
    for (const part of this.parts) {
      part.mesh.boundingSphere = this.bounds;
      this.root.add(part.mesh);
    }
    if (this.glow) {
      this.glow.mesh.boundingSphere = this.bounds;
      this.root.add(this.glow.mesh);
    }
    this.spinOffset.copy(spinGroup ? spinGroup.position : _p.set(0, 0, 0));
  }

  /** live coins */
  get count(): number { return this.ids.length; }

  has(id: number): boolean { return this.slots.has(id); }

  /** Adds a coin, or moves it if `id` is already in the field. `scale` shrinks or grows it (e.g. a pickup pop). */
  add(id: number, x: number, y: number, z: number, phase = id, scale = 1): void {
    let slot = this.slots.get(id);
    if (slot === undefined) {
      if (this.ids.length >= this.capacity) this.grow();
      slot = this.ids.length;
      this.ids.push(id);
      this.slots.set(id, slot);
    }
    this.pos[slot * 3] = x;
    this.pos[slot * 3 + 1] = y;
    this.pos[slot * 3 + 2] = z;
    this.phase[slot] = phase;
    this.scale[slot] = scale;
  }

  /** Removes a coin (the last one takes its slot). */
  remove(id: number): void {
    const slot = this.slots.get(id);
    if (slot === undefined) return;
    const last = this.ids.length - 1;
    const lastId = this.ids[last];
    if (slot !== last) {
      this.ids[slot] = lastId;
      this.slots.set(lastId, slot);
      this.pos.copyWithin(slot * 3, last * 3, last * 3 + 3);
      this.phase[slot] = this.phase[last];
      this.scale[slot] = this.scale[last];
    }
    this.ids.pop();
    this.slots.delete(id);
  }

  clear(): void {
    this.ids.length = 0;
    this.slots.clear();
  }

  /** Shows or hides the ETH coins' soft blob shadow (the scene draws it on low quality only). */
  setBlobVisible(visible: boolean): void {
    for (const part of this.parts) if (part.mesh.name === "blobShadow") part.mesh.visible = visible;
  }

  /** Writes every live instance's matrices for time `t` (seconds) and refits the shared bounding sphere. */
  update(t: number): void {
    const n = this.ids.length;
    const sol = this.kind === "sol";
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity, maxK = 1;
    for (let i = 0; i < n; i++) {
      const x = this.pos[i * 3], z = this.pos[i * 3 + 2], ph = this.phase[i], k = this.scale[i];
      if (k > maxK) maxK = k;
      const y = this.pos[i * 3 + 1] + (sol ? Math.sin(t * 5 + ph) * 0.06 : 0);
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
      const angle = sol ? t * 5.5 + ph * 0.7 : Math.sin(t * 2 + ph) * 0.35;
      // root: position + scale; spin: root × (offset, turn about y)
      _base.makeScale(k, k, k).setPosition(x, y, z);
      _q.setFromAxisAngle(_axisY, angle);
      _spin.compose(_p.copy(this.spinOffset), _q, _s.set(1, 1, 1)).premultiply(_base);
      for (const part of this.parts) {
        const frame = part.spins ? _spin : _base;
        if (part.identity) part.mesh.setMatrixAt(i, frame);
        else part.mesh.setMatrixAt(i, _m.multiplyMatrices(frame, part.local));
      }
      if (this.glow) {
        const { spec } = this.glow;
        const size = spec.size * k * this.glowBoost * (sol ? 1 : 0.88 + 0.14 * Math.sin(t * 6 + ph));
        _p.copy(spec.offset).multiplyScalar(k).add(_s.set(x, y, z));
        this.glow.mesh.setMatrixAt(i, _m.makeScale(size, size, size).setPosition(_p));
      }
    }
    for (const part of this.parts) this.commit(part.mesh, n);
    if (this.glow) this.commit(this.glow.mesh, n);
    // one sphere round every instance (+ the parts' and glow's reach): frustum culling stays correct as coins move
    if (n === 0) this.bounds.set(_p.set(0, -1e4, 0), 0);
    else {
      _sphere.center.set((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
      _sphere.radius = Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) / 2 + this.reach * maxK;
      this.bounds.copy(_sphere);
    }
  }

  private commit(mesh: THREE.InstancedMesh, n: number): void {
    mesh.count = n;
    mesh.instanceMatrix.clearUpdateRanges();
    mesh.instanceMatrix.addUpdateRange(0, n * 16);
    mesh.instanceMatrix.needsUpdate = true;
  }

  /** Doubles the capacity: new instanced meshes, the live matrices copied over. */
  private grow(): void {
    const capacity = this.capacity * 2;
    const swap = (old: THREE.InstancedMesh): THREE.InstancedMesh => {
      const mesh = new THREE.InstancedMesh(old.geometry, old.material, capacity);
      mesh.name = old.name;
      mesh.renderOrder = old.renderOrder;
      mesh.castShadow = old.castShadow;
      mesh.receiveShadow = old.receiveShadow;
      mesh.visible = old.visible;
      mesh.boundingSphere = this.bounds;
      (mesh.instanceMatrix.array as Float32Array).set(old.instanceMatrix.array as Float32Array);
      mesh.count = old.count;
      this.root.add(mesh);
      this.root.remove(old);
      old.dispose();
      return mesh;
    };
    for (const part of this.parts) part.mesh = swap(part.mesh);
    if (this.glow) this.glow.mesh = swap(this.glow.mesh);
    const grow = <T extends Float32Array>(a: T, size: number): Float32Array => { const b = new Float32Array(capacity * size); b.set(a); return b; };
    this.pos = grow(this.pos, 3);
    this.phase = grow(this.phase, 1);
    this.scale = grow(this.scale, 1);
    this.capacity = capacity;
  }

  /** Frees the instance buffers and the glow material (the coin geometry and materials are shared, they stay). */
  /** Night: the glow quads grow by `boost` and get brighter (1 = day, exactly as before). */
  setGlowBoost(boost: number): void {
    this.glowBoost = boost;
    if (!this.glow) return;
    const material = this.glow.mesh.material as THREE.ShaderMaterial;
    material.uniforms.opacity.value = Math.min(1, this.glow.spec.material.opacity * boost);
  }

  dispose(): void {
    for (const part of this.parts) part.mesh.dispose();
    if (this.glow) { this.glow.mesh.dispose(); this.glow.mesh.geometry.dispose(); (this.glow.mesh.material as THREE.Material).dispose(); }
    this.root.clear();
  }
}
